/**
 * Nexus Graph Metrics
 *
 * Pure catalog + types for configurable Nexus dashboard/contacts graph
 * panels (FEATURE-01/02/03). Keeps metric definitions, chart-type
 * compatibility, and panel config serialization in one testable module,
 * decoupled from data fetching and rendering.
 *
 * Primary responsibilities:
 * - Define the available metrics and which chart types each supports
 * - Provide default panel layouts for the dashboard and contacts pages
 * - Serialize/deserialize saved panel layouts for workspace preferences
 *
 * Dependencies:
 * - None (pure module)
 */

export type NexusChartType = 'line' | 'bar' | 'pie' | 'funnel' | 'area'

export type NexusDateRangePreset = '7d' | '30d' | '90d' | 'custom'

export type NexusMetricKey =
  | 'contacts_added'
  | 'contacts_by_status'
  | 'contacts_by_source'
  | 'campaign_open_rate'
  | 'campaign_click_rate'
  | 'campaign_reply_rate'
  | 'email_sent_volume'
  | 'sms_sent_volume'
  | 'bookings_created'
  | 'bookings_cancelled'
  | 'agent_conversation_volume'
  | 'agent_avg_response_time'
  | 'revenue_pipeline_value'
  | 'leads_by_stage_funnel'

export type NexusMetricDefinition = {
  key: NexusMetricKey
  label: string
  // Chart types that render this metric meaningfully.
  supportedChartTypes: NexusChartType[]
  defaultChartType: NexusChartType
  // 'timeseries' metrics vary over the date range; 'breakdown' metrics are
  // a single-point categorical split.
  kind: 'timeseries' | 'breakdown'
}

export const NEXUS_METRICS: readonly NexusMetricDefinition[] = [
  {
    key: 'contacts_added',
    label: 'Contacts added over time',
    supportedChartTypes: ['line', 'area', 'bar'],
    defaultChartType: 'line',
    kind: 'timeseries'
  },
  {
    key: 'contacts_by_status',
    label: 'Contacts by status',
    supportedChartTypes: ['pie', 'bar'],
    defaultChartType: 'pie',
    kind: 'breakdown'
  },
  {
    key: 'contacts_by_source',
    label: 'Contacts by source',
    supportedChartTypes: ['pie', 'bar'],
    defaultChartType: 'bar',
    kind: 'breakdown'
  },
  {
    key: 'campaign_open_rate',
    label: 'Campaign open rate',
    supportedChartTypes: ['line', 'area'],
    defaultChartType: 'line',
    kind: 'timeseries'
  },
  {
    key: 'campaign_click_rate',
    label: 'Campaign click rate',
    supportedChartTypes: ['line', 'area'],
    defaultChartType: 'line',
    kind: 'timeseries'
  },
  {
    key: 'campaign_reply_rate',
    label: 'Campaign reply rate',
    supportedChartTypes: ['line', 'area'],
    defaultChartType: 'line',
    kind: 'timeseries'
  },
  {
    key: 'email_sent_volume',
    label: 'Email sent volume',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultChartType: 'bar',
    kind: 'timeseries'
  },
  {
    key: 'sms_sent_volume',
    label: 'SMS sent volume',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultChartType: 'bar',
    kind: 'timeseries'
  },
  {
    key: 'bookings_created',
    label: 'Bookings created over time',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultChartType: 'bar',
    kind: 'timeseries'
  },
  {
    key: 'bookings_cancelled',
    label: 'Bookings cancelled over time',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultChartType: 'bar',
    kind: 'timeseries'
  },
  {
    key: 'agent_conversation_volume',
    label: 'Agent conversation volume',
    supportedChartTypes: ['bar', 'line', 'area'],
    defaultChartType: 'bar',
    kind: 'timeseries'
  },
  {
    key: 'agent_avg_response_time',
    label: 'Agent average response time',
    supportedChartTypes: ['line', 'area'],
    defaultChartType: 'line',
    kind: 'timeseries'
  },
  {
    key: 'revenue_pipeline_value',
    label: 'Revenue pipeline value over time',
    supportedChartTypes: ['area', 'line', 'bar'],
    defaultChartType: 'area',
    kind: 'timeseries'
  },
  {
    key: 'leads_by_stage_funnel',
    label: 'Leads by stage funnel',
    supportedChartTypes: ['funnel', 'bar'],
    defaultChartType: 'funnel',
    kind: 'breakdown'
  }
] as const

const METRIC_BY_KEY = new Map(NEXUS_METRICS.map((metric) => [metric.key, metric]))

export type NexusGraphFilters = {
  dateRange: NexusDateRangePreset
  customStart?: string
  customEnd?: string
  status?: string
  source?: string
  tag?: string
  campaign?: string
  stage?: string
  // Contacts page: sync the panel filters with the active list filters.
  syncWithListFilters?: boolean
}

export type NexusGraphPanelConfig = {
  id: string
  title: string
  metric: NexusMetricKey
  chartType: NexusChartType
  // Optional second metric overlaid on the same chart (split view).
  split?: NexusMetricKey
  filters: NexusGraphFilters
}

export type NexusGraphLayout = {
  version: 1
  panels: NexusGraphPanelConfig[]
}

// --------------

export function getNexusMetric(key: NexusMetricKey): NexusMetricDefinition | undefined {
  return METRIC_BY_KEY.get(key)
}

export function isChartTypeSupported(key: NexusMetricKey, chartType: NexusChartType): boolean {
  return getNexusMetric(key)?.supportedChartTypes.includes(chartType) ?? false
}

// Coerce a chart type to one the metric supports (falls back to its default).
export function resolveChartType(key: NexusMetricKey, chartType: NexusChartType): NexusChartType {
  const metric = getNexusMetric(key)
  if (!metric) return chartType
  return metric.supportedChartTypes.includes(chartType) ? chartType : metric.defaultChartType
}

let panelIdCounter = 0
function nextPanelId(prefix: string): string {
  panelIdCounter += 1
  return `${prefix}-${panelIdCounter}`
}

function makePanel(
  metric: NexusMetricKey,
  overrides: Partial<Omit<NexusGraphPanelConfig, 'metric'>> = {}
): NexusGraphPanelConfig {
  const def = getNexusMetric(metric)
  return {
    id: overrides.id ?? nextPanelId('panel'),
    title: overrides.title ?? def?.label ?? metric,
    metric,
    chartType: overrides.chartType ?? def?.defaultChartType ?? 'line',
    split: overrides.split,
    filters: overrides.filters ?? { dateRange: '30d' }
  }
}

// Dashboard default: 2 panels side by side (Contacts Added + Bookings).
export function getDefaultDashboardLayout(): NexusGraphLayout {
  return {
    version: 1,
    panels: [makePanel('contacts_added'), makePanel('bookings_created')]
  }
}

// Contacts page default: Contact Growth (line) + Status Breakdown (pie).
export function getDefaultContactsLayout(): NexusGraphLayout {
  return {
    version: 1,
    panels: [
      makePanel('contacts_added', { title: 'Contact growth', chartType: 'line' }),
      makePanel('contacts_by_status', { title: 'Status breakdown', chartType: 'pie' })
    ]
  }
}

// --------------

export function serializeNexusGraphLayout(layout: NexusGraphLayout): string {
  return JSON.stringify(layout)
}

export function parseNexusGraphLayout(
  raw: unknown,
  fallback: NexusGraphLayout
): NexusGraphLayout {
  let value: unknown = raw
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw)
    } catch {
      return fallback
    }
  }

  if (!value || typeof value !== 'object') return fallback
  const candidate = value as { version?: unknown; panels?: unknown }
  if (candidate.version !== 1 || !Array.isArray(candidate.panels)) return fallback

  const panels = candidate.panels
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .map((p): NexusGraphPanelConfig | null => {
      const metric = p.metric as NexusMetricKey
      if (!METRIC_BY_KEY.has(metric)) return null
      const chartType = resolveChartType(
        metric,
        (p.chartType as NexusChartType) ?? getNexusMetric(metric)!.defaultChartType
      )
      const split =
        typeof p.split === 'string' && METRIC_BY_KEY.has(p.split as NexusMetricKey)
          ? (p.split as NexusMetricKey)
          : undefined
      const filters =
        p.filters && typeof p.filters === 'object'
          ? (p.filters as NexusGraphFilters)
          : { dateRange: '30d' as NexusDateRangePreset }
      return {
        id: typeof p.id === 'string' ? p.id : nextPanelId('panel'),
        title: typeof p.title === 'string' ? p.title : getNexusMetric(metric)!.label,
        metric,
        chartType,
        split,
        filters: { dateRange: filters.dateRange ?? '30d', ...filters }
      }
    })
    .filter((p): p is NexusGraphPanelConfig => p !== null)

  return panels.length > 0 ? { version: 1, panels } : fallback
}
